import React, { useState, useEffect, useRef } from 'react'
import { FaCheck, FaMobile, FaSms, FaEnvelope, FaWhatsapp, FaPaperclip, FaTimes } from 'react-icons/fa'
import { Box, Stack, Text, Checkbox, Tooltip, Textarea, Button, Loading, Badge, Icon, EmptyBox, SimplePopover, SimpleTable, ConfirmButton, Link, EmptyCard, ImageViewer, FileChooser } from '@/components/lib'

import api from '../../api'
import utils, { ALLOWED_MESSAGE_FILE_TYPES } from '../../utils/utils'
import useStore from '../../store'
import { MessageWizard } from '../trials/messages/Automatic'

const sendViaOptions = [
  { key: 'push', label: 'Push', icon: FaMobile, canUse: p => p?.pushAvailable },
  { key: 'email', label: 'Email', icon: FaEnvelope, canUse: p => p?.emailAvailable },
  { key: 'sms', label: 'SMS', icon: FaSms, canUse: p => p?.smsAvailable },
  { key: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, canUse: p => p?.whatsappAvailable }
]

export default function ParticipantMessenger ({ trial, participant }) {
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [messages, setMessages] = useState([])
  const [automaticMessages, setAutomaticMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [sendVia, setSendVia] = useState([])
  const [automaticMessageIsOpen, setAutomaticMessageIsOpen] = useState(false)
  // The message the scheduling wizard is opened on, and what it should do with
  // it: 'create' from scratch, 'edit' in place, or 'copy' into a new one.
  const [automaticMessageMode, setAutomaticMessageMode] = useState('create')
  const [automaticMessageToEdit, setAutomaticMessageToEdit] = useState(null)
  const [attachments, setAttachments] = useState([])
  const messagesContainerRef = useRef(null)
  const trialId = trial?._id
  const participantId = participant?._id
  const { updateTrial, team, anchors } = useStore()

  const scrollMessagesToBottom = () => {
    const el = messagesContainerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }

  const featureAllowed = (key) => {
    if (key === 'sms') return utils.hasFeature(team, 'sms')
    if (key === 'whatsapp') return utils.hasFeature(team, 'whatsapp')
    return true
  }

  const visibleSendViaOptions = sendViaOptions.filter(o => featureAllowed(o.key))

  useEffect(() => {
    getMessages(newMessages => {
      const newSendVia = []
      newMessages.forEach(m => {
        m.via?.forEach(v => {
          const option = sendViaOptions.find(o => o.key === v)
          if (!newSendVia.includes(v) && option?.canUse(participant)) newSendVia.push(v)
        })
      })
      if (!newSendVia.length && participant?.pushAvailable) newSendVia.push('push')
      setSendVia(newSendVia)

      // Only auto-scroll on the initial (non-poll) load.
      window.requestAnimationFrame(scrollMessagesToBottom)
    })
  }, [trialId, participant, participantId])

  // Poll for new messages every 30s while participant is selected
  useEffect(() => {
    if (!participantId) return
    const interval = setInterval(() => getMessages(null, true), 30_000)
    return () => clearInterval(interval)
  }, [participantId])

  function getMessages (cb, silent = false) {
    if (participantId) {
      if (!silent) setLoadingMessages(true)
      api.trials.getParticipantMessages(trialId, participantId, ({ messages, automaticMessages }) => {
        if (!silent) setLoadingMessages(false)
        setMessages(messages)
        setAutomaticMessages(automaticMessages)
        if (cb) cb(messages)
      })
    }
  }

  function createMessage () {
    setSendingMessage(true)
    const filteredSendVia = (sendVia || []).filter(v => featureAllowed(v))
    const payload = { message: newMessage, sendVia: filteredSendVia }
    if (attachments.length) payload.attachments = attachments.map(a => a.storedName)
    api.trials.createParticipantMessage(trial._id, participantId, payload, message => {
      setSendingMessage(false)
      setMessages(prev => [...prev, message])
      setNewMessage('')
      setAttachments([])
      window.requestAnimationFrame(scrollMessagesToBottom)
    }, err => {
      utils.toast('error', 'Unable to send message', err.message)
      setSendingMessage(false)
    })
  }

  function markAsRead (participantId) {
    api.trials.readParticipantMessages(trial._id, participantId, data => {
      updateTrial(trial._id, { unreadMessages: data.unreadTrialMessages })
      setMessages(data.messages)
    }, err => {
      utils.toast('error', `Unable to mark as read: ${err.message}`)
    })
  }

  const addAutomaticMessage = (message) => {
    setAutomaticMessages([...automaticMessages, message])
  }

  const replaceAutomaticMessage = (message) => {
    setAutomaticMessages(automaticMessages.map(m => m._id === message._id ? message : m))
  }

  const openAutomaticMessageWizard = (mode, message = null) => {
    setAutomaticMessageMode(mode)
    setAutomaticMessageToEdit(message)
    setAutomaticMessageIsOpen(true)
  }

  const deleteAutomaticMessage = (messageId) => {
    api.trials.deleteAutomaticMessage(trialId, messageId, () => {
      setAutomaticMessages(automaticMessages.filter(m => m._id !== messageId))
      utils.toast('success', 'Automatic message has been deleted')
    }, err => {
      utils.toast('error', err.message)
    })
  }

  // Function to parse message text and convert URLs to clickable links
  const parseMessageWithLinks = (message, isAdminSender = false) => {
    if (!message) return null

    // Regex to match URLs starting with https:// or http://
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = []
    let lastIndex = 0
    let match

    while ((match = urlRegex.exec(message)) !== null) {
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push(message.substring(lastIndex, match.index))
      }

      // Add the URL as a link
      const url = match[0]
      parts.push(
        <Link
          key={match.index}
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          color={isAdminSender ? 'fg.onAccent' : 'primary.fg'}
          textDecoration='underline'
        >
          {url}
        </Link>
      )

      lastIndex = match.index + url.length
    }

    // Add remaining text after the last URL
    if (lastIndex < message.length) {
      parts.push(message.substring(lastIndex))
    }

    // If no URLs were found, return the original message
    if (parts.length === 0) {
      return message
    }

    return parts
  }

  return (
    <Stack gap={5} height={600} display='flex' flexDirection='column'>
      {!participant
        ? (
          <Stack direction='row' alignItems='center' justifyContent='center'>
            <EmptyCard icon='messages' title='Participant messenger' description='Send and receive messages with your participants.' />
          </Stack>
          )
        : (
          <>
            <Box display='flex' justifyContent='end' alignItems='center' gap={3} flexShrink={0}>
              <Stack direction='row' gap={1}>
                {messages?.filter(m => !m.isRead)?.length > 0 &&
                  <Button size='xs' onClick={e => markAsRead(participant._id)}><Icon as={FaCheck} /> Mark all as read</Button>}
              </Stack>
            </Box>

            {automaticMessages?.length > 0 &&
              <Box flexShrink={0}>
                <Stack direction='row' justifyContent='space-between' p={3} rounded='md' bg='bg.subtle' alignItems='center'>
                  <Text fontWeight='bold' fontSize='sm'>{automaticMessages?.length} scheduled messages</Text>
                  <SimplePopover
                    on='click' closeOnBlur={false}
                    trigger={<Button size='xs'>Manage</Button>}
                    header={<Box>Scheduled automatic messages for this participant</Box>}
                    body={
                      <Box>
                        <SimpleTable
                          headers={['Message', 'Scheduled at', '']}
                          rows={automaticMessages.map(m => {
                            return [
                              m.message,
                              utils.getAutomaticMessageScheduleAsString(m, null, anchors),
                              <Stack direction='row' gap={1} key={m._id}>
                                <Button size='xs' variant='outline' onClick={e => openAutomaticMessageWizard('edit', m)}>Edit</Button>
                                <Button size='xs' variant='outline' onClick={e => openAutomaticMessageWizard('copy', m)}>Copy</Button>
                                <ConfirmButton
                                  header='Really delete this scheduled message?'
                                  body={'It can\'t be recovered but you can make a new one instead'}
                                  trigger={<Button size='xs' colorPalette='red' variant='outline'>Delete</Button>}
                                  onConfirm={e => deleteAutomaticMessage(m._id)}
                                />
                              </Stack>]
                          })}
                          tableProps={{ size: 'sm' }}
                        />
                      </Box>
                    }
                    contentProps={{ w: '400px' }}
                  />
                </Stack>
              </Box>}

            <Box
              ref={messagesContainerRef}
              flex={1}
              overflowY='auto'
              display='flex'
              flexDirection='column'
              gap={3}
              minHeight={0}
            >
              {!loadingMessages && !messages?.length && <EmptyBox icon='messages' title='Participant messages' description='Messages will appear here once sent or received.' />}
              {loadingMessages && <Loading />}
              {messages?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))?.map(m =>
                <Box key={m._id} display='flex' justifyContent={m.sender === 'admin' ? 'end' : 'start'} mb={3}>
                  <Stack
                    width={{ md: '70%' }}
                    p={4}
                    borderRadius={18}
                    boxShadow={m.sender === 'admin' ? 'md' : 'sm'}
                    position='relative'
                    transition='all 0.2s ease'
                    _hover={{ boxShadow: m.sender === 'admin' ? 'lg' : 'md' }}
                    bg={m.sender === 'admin' ? 'primary.solid' : 'bg.surface'}
                    {...(m.sender === 'admin'
                      ? {
                          bgGradient: 'to-br',
                          gradientFrom: 'primary.solid',
                          gradientTo: 'primary.emphasized',
                          color: 'fg.onAccent'
                        }
                      : {
                          border: '1px solid',
                          borderColor: 'border.default'
                        })}
                    color={m.sender === 'admin' ? 'fg.onAccent' : undefined}
                  >
                    <Box display='flex' alignItems='baseline'>
                      <Stack direction='row' alignItems='center' gap={1}>
                        <Text fontSize='xs' color={m.sender === 'admin' ? 'fg.onAccent' : 'fg.muted'} fontWeight='medium'>
                          {utils.formatDateTime(m.createdAt)}
                        </Text>
                        {m.via?.map(v => {
                          if (!['sms', 'email', 'push', 'whatsapp'].includes(v)) return null
                          let icon
                          if (v === 'sms') icon = FaSms
                          if (v === 'email') icon = FaEnvelope
                          if (v === 'push') icon = FaMobile
                          if (v === 'whatsapp') icon = FaWhatsapp
                          return (
                            <Tooltip key={v} content={v}>
                              <Icon
                                color={m.sender === 'admin' ? 'fg.onAccent' : 'fg.subtle'}
                                as={icon}
                                fontSize='xs'
                              />
                            </Tooltip>
                          )
                        })}
                        {!m.isRead && <Badge colorPalette='yellow' size='sm'>Unread</Badge>}
                      </Stack>
                    </Box>

                    <Text
                      whiteSpace='pre-line'
                      color={m.sender === 'admin' ? 'fg.onAccent' : 'fg.default'}
                      fontSize='sm'
                      lineHeight='1.5'
                    >
                      {parseMessageWithLinks(m.message, m.sender === 'admin')}
                    </Text>

                    {m.fileUrls?.length > 0 &&
                      <Stack gap={2} direction='row' flexWrap='wrap'>
                        {m.fileUrls.map(f => {
                          const url = typeof f === 'string' ? f : f.url
                          const name = typeof f === 'string' ? url.split('/').pop() : f.name
                          const isImage = typeof f === 'object' ? f.contentType?.startsWith('image/') : true
                          if (isImage) return <ImageViewer key={url} url={url} withPreview />
                          return (
                            <Button size='xs' variant='outline' key={url} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                              <Icon as={FaPaperclip} mr={1} />{name}
                            </Button>
                          )
                        })}
                      </Stack>}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box p={3} bg='bg.surface' rounded='md' flexShrink={0}>
              <Textarea placeholder={`Write a new message to ${participant.id}...`} value={newMessage} onChange={e => setNewMessage(e.target.value)} />
              {attachments.length > 0 &&
                <Stack>
                  <Stack direction='row' flexWrap='wrap' gap={2} mt={2}>
                    {attachments.map((a, i) => (
                      <Box key={i} display='flex' alignItems='center' gap={1} px={2} py={1} bg='bg.subtle' rounded='md' fontSize='xs'>
                        <Icon as={FaPaperclip} />
                        <Text fontSize='xs'>{a.name}</Text>
                        <Icon as={FaTimes} cursor='pointer' onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} />
                      </Box>
                    ))}
                  </Stack>
                  <Text fontSize='xs' color='fg.muted'>Attachments are currently supported via email and SMS.</Text>
                </Stack>}
              <Stack alignItems='end'>

                <Stack direction='row' gap={2} alignItems='center'>
                  <Text fontSize='xs' fontWeight='bold'>Send via:</Text>
                  {visibleSendViaOptions.map(({ key, label, icon, canUse }) => {
                    const disabled = !canUse(participant)
                    return (
                      <Checkbox disabled={disabled} size='xs' key={key} checked={sendVia?.includes(key)} onChange={e => e.checked ? setSendVia([...sendVia, key]) : setSendVia(sendVia.filter(v => v !== key))}>
                        <Tooltip content={disabled ? 'Not available for this participant' : 'This is available'}>
                          <Box>
                            {label}
                          </Box>
                        </Tooltip>
                      </Checkbox>
                    )
                  })}
                  <FileChooser
                    forType='participant'
                    forObject={{ _id: participantId }}
                    accept={ALLOWED_MESSAGE_FILE_TYPES}
                    multiple
                    onComplete={file => setAttachments(prev => [...prev, { storedName: file.storedName, name: file.name }])}
                    onError={err => utils.toast('error', `Upload failed: ${err}`)}
                    trigger={<Button size='xs' variant='ghost'><Icon as={FaPaperclip} mr={1} /> Attach</Button>}
                  />
                </Stack>
                <Stack direction='row' gap={2} alignItems='center'>
                  <Button colorPalette='primary' variant='ghost' onClick={e => openAutomaticMessageWizard('create')}>Schedule a message</Button>
                  <MessageWizard
                    trialId={trialId} participant={participant} isOpen={automaticMessageIsOpen}
                    mode={automaticMessageMode} message={automaticMessageToEdit}
                    onClose={() => setAutomaticMessageIsOpen(false)}
                    onCreated={addAutomaticMessage} onUpdated={replaceAutomaticMessage}
                  />
                  <Button onClick={createMessage} isLoading={sendingMessage} colorPalette='primary'>Send message</Button>
                </Stack>
              </Stack>
            </Box>
          </>)}
    </Stack>
  )
}
